<template>
  <Transition name="fade">
    <div
      v-if="show"
      class="fixed inset-0 z-50 flex items-center justify-center"
    >
      <!-- Overlay -->
      <div
        class="absolute inset-0 bg-black/50"
        @click="$emit('cancel')"
      ></div>

      <!-- Modal -->
      <div
        class="relative bg-white rounded-lg shadow-lg p-6 max-w-sm w-full mx-4"
      >
        <!-- Title -->
        <h2 class="text-lg font-semibold text-gray-900 mb-3">
          {{ title }}
        </h2>
        <!-- 💡 SLOT for custom body (like notice) -->
        <slot name="body"></slot>

        <!-- Description -->
        <p class="text-sm text-gray-600 mb-4">
          {{ description }}
        </p>

        <!-- Names List (if provided) -->
        <div
          v-if="names && names.length > 0"
          class="mb-4 bg-gray-50 rounded p-3"
        >
          <p class="text-xs font-medium text-gray-700 mb-2">{{ itemLabel }}:</p>
          <ul class="text-sm text-gray-600 space-y-1">
            <li
              v-for="(name, index) in displayedNames"
              :key="index"
              class="flex items-start"
            >
              <span class="text-gray-400 mr-2">•</span>
              <span>{{ name }}</span>
            </li>
          </ul>
          <p
            v-if="remainingCount > 0"
            class="text-xs text-gray-500 mt-2"
          >
            ... and {{ remainingCount }} more
          </p>
        </div>

        <!-- Buttons -->
        <div class="flex gap-3 justify-end">
          <button
            @click="$emit('cancel')"
            :disabled="isLoading"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            @click="$emit('confirm')"
            :disabled="isLoading"
            :class="[
              'px-4 py-2 text-sm font-medium text-white rounded-md transition disabled:opacity-50 disabled:cursor-not-allowed',
              isDestructive
                ? 'bg-red-600 hover:bg-red-700'
                : 'bg-blue-600 hover:bg-blue-700',
            ]"
          >
            <span v-if="isLoading">Loading...</span>
            <span v-else>{{ buttonText }}</span>
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup>
  import { computed } from "vue";

  const props = defineProps({
    show: {
      type: Boolean,
      required: true,
    },
    title: {
      type: String,
      default: "Confirm Action",
    },
    description: {
      type: String,
      default: "Are you sure?",
    },
    names: {
      type: Array,
      default: () => [],
    },
    itemLabel: {
      type: String,
      default: "Items",
    },
    buttonText: {
      type: String,
      default: "Confirm",
    },
    isDestructive: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  });

  const displayedNames = computed(() => {
    return props.names?.slice(0, 3) || [];
  });

  const remainingCount = computed(() => {
    return Math.max(0, (props.names?.length || 0) - 3);
  });

  defineEmits(["confirm", "cancel"]);
</script>

<style scoped>
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.2s ease;
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }
</style>
